import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import SendIcon from '@material-ui/icons/Send';
import { makeStyles } from '@material-ui/core/styles';

import { normalizePhone } from '../../../constants/whatsapp-contacts';
import { actions as whatsappActions } from '../actions';
import { colors } from '../theme';

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...whatsappActions }, dispatch);

const useStyles = makeStyles((theme) => ({
  note: {
    color: colors.textDim,
    fontSize: '13px',
    lineHeight: 1.45,
    marginBottom: theme.spacing(2),
  },
  ok: {
    color: colors.okText,
    fontWeight: 600,
    marginTop: theme.spacing(1.5),
  },
  error: {
    color: '#d32f2f',
    marginTop: theme.spacing(1.5),
  },
  sendButton: {
    backgroundColor: colors.accent,
    color: '#ffffff',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: colors.accentHover,
    },
  },
}));

const newRequestKey = () =>
  `${Date.now()}${Math.random().toString(36).slice(2)}`.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);

// Пробная отправка: то же сообщение, как его увидит клиент, но на один свой номер. В «Истории»
// она отмечена как пробная и не считается рассылкой клиенту, поэтому ночью и при лимите частоты
// её не останавливают.
function TestSendDialog(props) {
  const classes = useStyles();
  const { open, onClose, template, params, name } = props;

  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const requestKey = useRef(newRequestKey());

  useEffect(() => {
    if (open) {
      requestKey.current = newRequestKey();
    } else {
      setBusy(false);
      setError('');
      setSentTo('');
    }
  }, [open]);

  const handleSend = async () => {
    const digits = normalizePhone(phone);
    if (!digits) {
      setError('Введите номер в формате +380 50 123 45 67.');
      return;
    }
    setBusy(true);
    setError('');
    setSentTo('');
    const result = await props.sendBroadcast({
      recipients: [{ phone: digits, params }],
      template: {
        name: template.templateName,
        language: template.language,
        consentButtons: Boolean(template.consentButtons),
      },
      name: `Пробная отправка: ${name || template.title}`,
      test: true,
      requestKey: requestKey.current,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    // Следующая отправка на тот же номер это новая пробная, а не повтор этой.
    requestKey.current = newRequestKey();
    setSentTo(`+${digits}`);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Отправить себе для проверки</DialogTitle>
      <DialogContent>
        <Typography className={classes.note}>
          Сообщение уйдёт на один ваш номер ровно так, как его получит клиент: с подставленным именем и
          картинкой. Это не рассылка: клиентам ничего не отправляется. Пробная отправка видна в разделе
          «История», и за неё платят как за обычное сообщение.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          size="small"
          label="Ваш номер"
          placeholder="+380 50 123 45 67"
          value={phone}
          disabled={busy}
          onChange={(event) => {
            setPhone(event.target.value);
            setError('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') handleSend();
          }}
        />
        {sentTo && (
          <Typography variant="body2" className={classes.ok}>
            Отправлено на {sentTo}. Сообщение должно прийти в течение минуты.
          </Typography>
        )}
        {error && (
          <Typography variant="body2" className={classes.error}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Закрыть
        </Button>
        <Button
          className={classes.sendButton}
          variant="contained"
          disableElevation
          disabled={busy}
          startIcon={<SendIcon />}
          onClick={handleSend}
        >
          Отправить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default connect(null, mapDispatchToProps)(TestSendDialog);
